import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Navigation from "../components/Navigation";
import InsightChat from "../components/InsightChat";
import { api, API } from "../lib/api";
import { Download, FileSpreadsheet, FileText, ChevronDown, ChevronRight, Play, Pause, X, Volume2 } from "lucide-react";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Legend, CartesianGrid } from "recharts";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import adrigoLogo from "../assets/adrigo-logo.png";

const SOURCE_COLORS = { internal: "#002EA6", cloudresearch: "#FF3B30", intercept: "#FFB800" };
const SOURCE_LABEL = { internal: "Internal", cloudresearch: "Programmatic", intercept: "Intercept" };
const QUANT_COLORS = ["#002EA6", "#FF3B30", "#FFB800", "#00C853", "#5E5F6E", "#111113"];

// Wave 12 — Emotional taxonomy: FIXED 5 categories so cross-campaign
// comparison stays meaningful. Colors sequenced from most-negative
// (red) → neutral (yellow) → most-positive (green) so the stacked
// bar visually reads as a sentiment gradient at a glance.
const EMOTION_LABELS = {
  frustration_helplessness: "Frustration / Helplessness",
  anxious_abandonment: "Anxious Abandonment",
  empathy_concern: "Empathy / Concern",
  resigned_acceptance: "Resigned Acceptance",
  hopeful: "Hopeful",
};
const EMOTION_COLORS = {
  frustration_helplessness: "#B00020",
  anxious_abandonment: "#FF3B30",
  empathy_concern: "#FFB800",
  resigned_acceptance: "#5E5F6E",
  hopeful: "#00C853",
};
const DEMO_AXIS_LABELS = {
  age_bracket: "Age Bracket",
  gender: "Gender",
  province: "Province",
  provider_status: "Provider Status",
};
const DEMO_VALUE_LABELS = {
  female: "Female", male: "Male", nonbinary: "Non-binary",
  prefer_not_to_say: "Prefer not to say",
  attached: "Attached (has GP)", unattached: "Unattached / clinic",
  not_sure: "Not sure", outside_ca: "Outside Canada",
};
const prettyDemoValue = (v) => DEMO_VALUE_LABELS[v] || v;

// Multi-line, word-wrapped XAxis tick for the Quantitative bar charts.
// Recharts only supports single-line labels natively, and long option
// strings ("Nova Scotia economy & community", "Superior quality,
// freshness, or artisanal taste"…) get clipped when angled. This
// custom tick word-wraps into up to 3 lines centered under each bar,
// keeping the axis horizontal — cleaner and more premium than -35°.
// The per-line character budget is derived from the *bar count* so 8-bar
// charts never let one label bleed into a neighbour's slot.
const wrapLabel = (text, maxChars, maxLines) => {
  const words = String(text ?? "").split(/\s+/).filter(Boolean);
  const lines = [];
  let current = "";
  for (const w of words) {
    const tentative = current ? `${current} ${w}` : w;
    if (tentative.length > maxChars && current) {
      lines.push(current);
      current = w;
      if (lines.length === maxLines - 1) break;
    } else {
      current = tentative;
    }
  }
  if (current && lines.length < maxLines) lines.push(current);
  const consumed = lines.join(" ").split(/\s+/).filter(Boolean).length;
  if (consumed < words.length) {
    const last = lines[lines.length - 1] || "";
    lines[lines.length - 1] = last.length > maxChars - 1
      ? `${last.slice(0, maxChars - 1)}…`
      : `${last}…`;
  }
  return lines;
};
const makeAxisTick = (barCount) => {
  // Bar-count-aware sizing so 3-bar and 8-bar charts both look premium.
  const sizing = barCount <= 4
    ? { maxChars: 16, fontSize: 11, lineHeight: 13 }
    : barCount <= 6
    ? { maxChars: 12, fontSize: 10, lineHeight: 12 }
    : barCount <= 8
    ? { maxChars: 10, fontSize: 10, lineHeight: 12 }
    : { maxChars: 9,  fontSize: 9,  lineHeight: 11 };
  const AxisTick = ({ x, y, payload }) => {
    const lines = wrapLabel(payload?.value, sizing.maxChars, 3);
    return (
      <g transform={`translate(${x},${y + 4})`}>
        <text
          textAnchor="middle"
          fill="#111113"
          fontSize={sizing.fontSize}
          fontWeight={500}
          fontFamily="Arial, Helvetica, sans-serif"
          style={{ letterSpacing: "0.01em" }}
        >
          {lines.map((line, i) => (
            <tspan key={i} x={0} dy={i === 0 ? sizing.fontSize + 2 : sizing.lineHeight}>{line}</tspan>
          ))}
        </text>
      </g>
    );
  };
  return AxisTick;
};

export default function ExecutiveReport() {
  const { campaignId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [compiling, setCompiling] = useState(true);
  const [downloading, setDownloading] = useState(false);

  // Audio playback drawer for clickable quotes
  const [activeQuote, setActiveQuote] = useState(null); // { text, interview_id, audio_path }
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  const playQuote = async (q) => {
    if (!q.interview_id || !q.audio_path) return;
    // audio_path looks like "interviews/{id}/utterances/{filename}.ext"
    // our endpoint expects /api/files/audio/{interview_id}/{filename}
    const filename = q.audio_path.split("/").pop();
    setActiveQuote({ ...q, _filename: filename });
    setPlaying(true);
  };

  useEffect(() => {
    if (!activeQuote || !audioRef.current) return;
    audioRef.current.load();
    audioRef.current.play().catch(() => setPlaying(false));
  }, [activeQuote]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/campaigns/${campaignId}/report`);
        setData(res.data);
      } catch {
        // Report data load failure — `data` remains null and the empty-state UI is rendered below.
      } finally {
        setCompiling(false);
      }
    })();
  }, [campaignId]);

  const buildPptx = async () => {
    if (!data) return;
    setDownloading(true);
    try {
      const res = await api.get(`/campaigns/${campaignId}/report/pptx`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.presentationml.presentation" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `researchlocal_${(data.campaign.title || "report").replace(/\W+/g, "_")}.pptx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      // PPTX export failed — could surface a toast, but keeping silent until we add a toaster.
    } finally {
      setDownloading(false);
    }
  };

  // Verbatim Excel export — one-click .xlsx download of every open-ended
  // qualitative response across all verified interviews. Same
  // authenticated blob-download pattern as PPTX.
  const [xlsxBusy, setXlsxBusy] = useState(false);
  const buildVerbatimsXlsx = async () => {
    if (!data) return;
    setXlsxBusy(true);
    try {
      const res = await api.get(`/campaigns/${campaignId}/export-verbatims-excel`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `adrigo_verbatims_${(data.campaign.title || "report").replace(/\W+/g, "_")}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      // silent for now — matches PPTX behavior; the button re-enables via finally.
    } finally {
      setXlsxBusy(false);
    }
  };

  // PDF export — capture the live executive-report DOM (charts + text) and
  // stitch into a multi-page A4 PDF so the client can download the exact
  // view they see on-screen. Every page carries a branded header (study
  // title left, date right, Adrigo Insights logo top-right corner) and a
  // footer with "Page X of Y" (left) and the platform URL (centered).
  // `data-pdf-hide` on any node tells html2canvas to skip it (used to
  // strip the action-button row + floating chat).
  const reportRef = useRef(null);
  const [pdfBusy, setPdfBusy] = useState(false);

  // Load the Adrigo logo as a base64 data-URL once (jsPDF needs raw data).
  const loadImageAsDataUrl = (src) => new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const c = document.createElement("canvas");
      c.width = img.naturalWidth; c.height = img.naturalHeight;
      c.getContext("2d").drawImage(img, 0, 0);
      resolve({ dataUrl: c.toDataURL("image/png"), w: img.naturalWidth, h: img.naturalHeight });
    };
    img.onerror = reject;
    img.src = src;
  });

  const buildPdf = async () => {
    if (!data || !reportRef.current) return;
    setPdfBusy(true);
    try {
      // 1) Rasterise the whole report DOM into one tall canvas.
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#F5F5F7",
        ignoreElements: (el) => el.getAttribute && el.getAttribute("data-pdf-hide") === "true",
      });
      const imgData = canvas.toDataURL("image/jpeg", 0.92);
      const logo = await loadImageAsDataUrl(adrigoLogo);

      // 2) PDF geometry (A4 mm units).
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const HEADER_H = 20;
      const FOOTER_H = 13;
      const SIDE = 8;
      const contentH = pageH - HEADER_H - FOOTER_H;
      const imgW = pageW - SIDE * 2;
      const imgH = (canvas.height * imgW) / canvas.width;
      const totalPages = Math.max(1, Math.ceil(imgH / contentH));

      // Logo dims (preserve aspect, cap at ~13mm tall so it sits neatly in header).
      const LOGO_H = 12;
      const LOGO_W = LOGO_H * (logo.w / logo.h);

      const dateStr = new Date().toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
      const title = data.campaign?.title || "Executive Report";
      const platformUrl = "https://micro-research.adrigoinsights.com/";

      const drawChrome = (pageNum) => {
        // Mask any bleed of the report image into the header/footer bands
        // by painting solid white strips first, then draw the chrome text.
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, 0, pageW, HEADER_H, "F");
        pdf.rect(0, pageH - FOOTER_H, pageW, FOOTER_H, "F");

        // Header — logo top-right corner
        pdf.addImage(logo.dataUrl, "PNG", pageW - SIDE - LOGO_W, 4, LOGO_W, LOGO_H);
        // Study title (left) — subtle, tracks the report's tight-heading vibe
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(10);
        pdf.setTextColor(17, 17, 19);
        pdf.text(title, SIDE, 11, { baseline: "middle", maxWidth: pageW - SIDE * 2 - LOGO_W - 30 });
        // Date (right, to the left of the logo)
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8);
        pdf.setTextColor(94, 95, 110);
        pdf.text(dateStr, pageW - SIDE - LOGO_W - 3, 11, { align: "right", baseline: "middle" });
        // Thin divider line under header
        pdf.setDrawColor(229, 229, 234);
        pdf.setLineWidth(0.2);
        pdf.line(SIDE, HEADER_H - 2, pageW - SIDE, HEADER_H - 2);

        // Footer — divider on top, "Page X of Y" left, URL centered
        pdf.setDrawColor(229, 229, 234);
        pdf.line(SIDE, pageH - FOOTER_H + 2, pageW - SIDE, pageH - FOOTER_H + 2);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8);
        pdf.setTextColor(94, 95, 110);
        pdf.text(`Page ${pageNum} of ${totalPages}`, SIDE, pageH - 5);
        pdf.text(`Made with ${platformUrl}`, pageW / 2, pageH - 5, { align: "center" });
      };

      // 3) Paginate: slice the full-height report image across pages so
      // each page shows one strip inside the content band.
      for (let i = 0; i < totalPages; i++) {
        if (i > 0) pdf.addPage();
        const y = HEADER_H - i * contentH;
        pdf.addImage(imgData, "JPEG", SIDE, y, imgW, imgH, undefined, "FAST");
        drawChrome(i + 1);
      }

      const filename = `adrigo_report_${(data.campaign.title || "report").replace(/\W+/g, "_")}.pdf`;
      pdf.save(filename);
    } catch {
      // silent; button re-enables via finally.
    } finally {
      setPdfBusy(false);
    }
  };

  if (compiling) {
    return (
      <div className="min-h-screen bg-[var(--rl-bg-subtle)]" data-testid="report-loading">
        <Navigation />
        <div className="max-w-3xl mx-auto px-6 py-32 text-center">
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-[var(--rl-brand)]">Compiling</div>
          <h1 className="font-display font-black text-4xl mt-3">Building your executive report…</h1>
          <p className="text-[var(--rl-text-secondary)] mt-4">Aggregating transcripts, clustering themes, drafting your TL;DR.</p>
        </div>
      </div>
    );
  }

  if (!data) return <div className="p-12">No data.</div>;

  const ts = data.traffic_by_source || {};
  const pieData = Object.entries(ts).map(([k, v]) => ({ name: SOURCE_LABEL[k] || k, key: k, value: v }));

  return (
    <div className="min-h-screen bg-[var(--rl-bg-subtle)]" data-testid="executive-report-page">
      <Navigation />
      <div className="max-w-7xl mx-auto px-6 py-12" ref={reportRef}>
        <button onClick={() => nav(`/client/campaigns/${campaignId}`)} className="text-sm font-semibold text-[var(--rl-text-secondary)] hover:text-[var(--rl-text)] transition-colors" data-testid="back-to-campaign" data-pdf-hide="true">← Back to campaign</button>
        <div className="flex items-start justify-between flex-wrap gap-4 mt-3">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-[var(--rl-accent)]">Executive Report</div>
            <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tight mt-2">{data.campaign.title}</h1>
            <div className="text-sm text-[var(--rl-text-secondary)] mt-2">{data.verified_count} verified completes · {data.campaign.status}</div>
          </div>
          <div className="flex flex-wrap items-center gap-2" data-pdf-hide="true">
            <button
              onClick={buildVerbatimsXlsx}
              disabled={xlsxBusy}
              className="inline-flex items-center gap-2 bg-white border border-[var(--rl-text)] text-[var(--rl-text)] px-5 py-3 font-semibold hover:bg-[var(--rl-text)] hover:text-white transition-colors disabled:opacity-50"
              data-testid="export-verbatims-xlsx-btn"
              title="Download every open-ended response as a structured Excel spreadsheet"
            >
              <FileSpreadsheet className="w-4 h-4" /> {xlsxBusy ? "Compiling verbatims…" : "Export Verbatims (.xlsx)"}
            </button>
            <button
              onClick={buildPptx}
              disabled={downloading}
              className="inline-flex items-center gap-2 bg-[var(--rl-accent)] text-white px-5 py-3 font-semibold hover:bg-[var(--rl-text)] transition-colors disabled:opacity-50"
              data-testid="export-pptx-btn"
            >
              <Download className="w-4 h-4" /> {downloading ? "Compiling data…" : "Export Editable PowerPoint"}
            </button>
            <button
              onClick={buildPdf}
              disabled={pdfBusy}
              className="inline-flex items-center gap-2 bg-[var(--rl-text)] text-white px-5 py-3 font-semibold hover:bg-[var(--rl-brand)] transition-colors disabled:opacity-50"
              data-testid="export-pdf-btn"
              title="Download this executive report as a PDF, exactly as shown on screen"
            >
              <FileText className="w-4 h-4" /> {pdfBusy ? "Rendering PDF…" : "Download PDF"}
            </button>
          </div>
        </div>

        {/* Top row: TL;DR + Traffic */}
        <div className="grid lg:grid-cols-3 gap-6 mt-10">
          <div className="lg:col-span-2 bg-[var(--rl-text)] text-white p-8" data-testid="tldr-card">
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[var(--rl-accent)]">Executive TL;DR</div>
            <div className="mt-6 space-y-5">
              {(data.qual?.tldr || []).length === 0 ? (
                <div className="text-white/60">More responses are needed before we can synthesize a narrative.</div>
              ) : data.qual.tldr.map((b, i) => (
                <div key={`tldr-${i}-${b.slice(0, 16)}`} className="flex gap-4">
                  <div className="font-display font-black text-3xl text-[var(--rl-accent)] flex-shrink-0">{i + 1}</div>
                  <div className="text-base leading-relaxed">{b}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white border border-[var(--rl-border)] p-6" data-testid="traffic-pie-card">
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[var(--rl-text-secondary)]">Traffic by Channel</div>
            <div className="h-56 mt-3">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={2}>
                    {pieData.map((e) => <Cell key={e.key} fill={SOURCE_COLORS[e.key] || "#5E5F6E"} />)}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Wave 12 — Participant Demographic Snapshot */}
        {data.demographic_snapshot && (
          <div className="mt-10" data-testid="demographic-snapshot-section">
            <div className="flex items-baseline justify-between mb-4">
              <div className="text-xs uppercase tracking-[0.3em] font-bold text-[var(--rl-brand)]">Participant Demographic Snapshot</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--rl-text-secondary)]" data-testid="demographic-completeness">
                {data.demographic_snapshot.demographics_captured} / {data.demographic_snapshot.total_interviews} respondents ({data.demographic_snapshot.completeness_pct}%) provided data
              </div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { key: "age", label: "Age", data: data.demographic_snapshot.age, testid: "demo-card-age" },
                { key: "gender", label: "Gender", data: data.demographic_snapshot.gender, testid: "demo-card-gender" },
                { key: "province", label: "Province / Region", data: data.demographic_snapshot.province, testid: "demo-card-province" },
                { key: "provider_status", label: "Provider Status", data: data.demographic_snapshot.provider_status, testid: "demo-card-provider" },
              ].map((card) => (
                <div key={card.key} className="bg-white border border-[var(--rl-border)] p-5" data-testid={card.testid}>
                  <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[var(--rl-text-secondary)] mb-3">{card.label}</div>
                  {(card.data || []).length === 0 ? (
                    <div className="text-xs text-[var(--rl-text-secondary)]">Not yet captured</div>
                  ) : (
                    <div className="space-y-2">
                      {card.data.slice(0, 8).map((row) => (
                        <div key={row.key} className="text-xs">
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-semibold">{prettyDemoValue(row.key)}</span>
                            <span className="font-mono text-[var(--rl-text-secondary)]">{row.count} · {row.percentage}%</span>
                          </div>
                          <div className="h-1.5 bg-[var(--rl-bg-subtle)]">
                            <div className="h-full bg-[var(--rl-brand)]" style={{ width: `${row.percentage}%`, transition: "width 400ms ease" }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quant charts */}
        <div className="mt-10">
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-[var(--rl-brand)] mb-4">Quantitative</div>
          {Object.keys(data.quant || {}).length === 0 ? (
            <div className="bg-white border border-[var(--rl-border)] p-8 text-sm text-[var(--rl-text-secondary)]">No multiple-choice data captured for this study.</div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6" data-testid="quant-charts">
              {Object.entries(data.quant).map(([q, counts]) => {
                const chartData = Object.entries(counts).map(([name, value]) => ({ name, value }));
                return (
                  <div key={q} className="bg-white border border-[var(--rl-border)] p-6">
                    <div className="font-semibold text-sm leading-snug">{q}</div>
                    <div className="h-80 mt-3">
                      <ResponsiveContainer>
                        <BarChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: 40 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E5E5EA" />
                          <XAxis dataKey="name" interval={0} tick={makeAxisTick(chartData.length)} height={90} tickLine={false} stroke="#5E5F6E" />
                          <YAxis fontSize={11} stroke="#5E5F6E" allowDecimals={false} fontFamily="Arial, Helvetica, sans-serif" />
                          <Tooltip />
                          <Bar dataKey="value">
                            {chartData.map((_, i) => <Cell key={i} fill={QUANT_COLORS[i % QUANT_COLORS.length]} />)}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Wave 12 — Persona Crosstabs (LLM-picked pairs w/ takeaway) */}
        {data.crosstabs && (data.crosstabs.pairs?.length || data.crosstabs.takeaway) && (
          <div className="mt-10" data-testid="crosstabs-section">
            <div className="flex items-baseline justify-between mb-4">
              <div className="text-xs uppercase tracking-[0.3em] font-bold text-[var(--rl-accent)]">Persona Crosstabs</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--rl-text-secondary)]">
                {data.crosstabs.eligible_count} respondents in analysis
              </div>
            </div>
            {(data.crosstabs.takeaway || data.crosstabs.primary_persona) && (
              <div className="bg-[var(--rl-text)] text-white p-6 mb-6" data-testid="crosstab-takeaway-card">
                <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[var(--rl-accent)]">Executive Crosstab Takeaway</div>
                {data.crosstabs.primary_persona?.label && (
                  <div className="font-display font-black text-2xl mt-2" data-testid="crosstab-primary-persona">
                    Highest-friction persona: {data.crosstabs.primary_persona.label}
                  </div>
                )}
                {data.crosstabs.takeaway && (
                  <div className="mt-3 text-base leading-relaxed">{data.crosstabs.takeaway}</div>
                )}
                {data.crosstabs.primary_persona?.why && (
                  <div className="mt-2 text-sm text-white/70">{data.crosstabs.primary_persona.why}</div>
                )}
              </div>
            )}
            <div className="grid md:grid-cols-2 gap-6" data-testid="crosstab-pairs">
              {(data.crosstabs.pairs || []).map((p, pi) => {
                const rows = Object.entries(p.table).map(([demoVal, answers]) => {
                  const total = Object.values(answers).reduce((s, n) => s + n, 0) || 1;
                  return { demoVal, answers, total };
                });
                const allAnswers = Array.from(new Set(rows.flatMap(r => Object.keys(r.answers))));
                return (
                  <div key={pi} className="bg-white border border-[var(--rl-border)] p-6" data-testid={`crosstab-pair-${pi}`}>
                    <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[var(--rl-text-secondary)]">
                      {DEMO_AXIS_LABELS[p.demographic_axis] || p.demographic_axis} × <span className="normal-case tracking-normal font-normal text-[var(--rl-text)]">{p.question}</span>
                    </div>
                    {p.headline && <div className="font-display font-bold text-lg mt-3">{p.headline}</div>}
                    {p.commentary && <div className="text-sm text-[var(--rl-text-secondary)] mt-2">{p.commentary}</div>}
                    <div className="mt-4 overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-[var(--rl-border)]">
                            <th className="text-left font-semibold p-1"></th>
                            {allAnswers.map((a) => (
                              <th key={a} className="text-right font-semibold p-1">{a}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((r) => (
                            <tr key={r.demoVal} className="border-b border-[var(--rl-border)]/50">
                              <td className="p-1 font-mono">{prettyDemoValue(r.demoVal)}</td>
                              {allAnswers.map((a) => {
                                const n = r.answers[a] || 0;
                                const pct = Math.round(100 * n / r.total);
                                return (
                                  <td key={a} className="p-1 text-right font-mono">
                                    <span className="text-[var(--rl-text-secondary)]">{n}</span>
                                    <span className="ml-1">({pct}%)</span>
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Thematic clustering */}
        <div className="mt-10">
          <div className="text-xs uppercase tracking-[0.3em] font-bold text-[var(--rl-accent)] mb-4">Thematic Clustering</div>
          <div className="bg-white border border-[var(--rl-border)] divide-y divide-[var(--rl-border)]" data-testid="themes-accordion">
            {(data.qual?.themes || []).length === 0 ? (
              <div className="p-8 text-sm text-[var(--rl-text-secondary)]">More qualitative responses are needed to surface themes.</div>
            ) : data.qual.themes.map((t, i) => (
              <div key={t.name || `theme-${i}`}>
                <button
                  onClick={() => setOpen(open === i ? null : i)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-[var(--rl-bg-subtle)] transition-colors"
                  data-testid={`theme-toggle-${i}`}
                >
                  <div className="flex-1">
                    <div className="font-display font-bold text-xl">{t.name}</div>
                    <div className="mt-2 h-1.5 bg-[var(--rl-bg-subtle)] max-w-md">
                      <div className="h-full bg-[var(--rl-accent)]" style={{ width: `${t.percentage}%`, transition: "width 400ms ease" }} />
                    </div>
                  </div>
                  <div className="flex items-center gap-3 ml-6">
                    <span className="font-mono text-sm font-bold">{t.percentage}%</span>
                    {open === i ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                  </div>
                </button>
                {open === i && (
                  <div className="px-6 pb-6 space-y-3" data-testid={`theme-quotes-${i}`}>
                    {(t.quotes || []).map((q, j) => {
                      // Quote may be a string (legacy) or an object with provenance (new)
                      const qObj = typeof q === "string" ? { text: q } : (q || {});
                      const hasAudio = !!(qObj.interview_id && qObj.audio_path);
                      return (
                        <button
                          key={`${t.name}-q-${j}`}
                          type="button"
                          onClick={() => hasAudio && playQuote(qObj)}
                          className={`w-full text-left bg-[var(--rl-bg-subtle)] p-4 border-l-2 ${hasAudio ? "border-[var(--rl-accent)] hover:bg-white cursor-pointer" : "border-[var(--rl-brand)] cursor-default"} transition-colors`}
                          data-testid={`theme-quote-${i}-${j}`}
                        >
                          <div className="text-sm italic leading-relaxed">"{qObj.text}"</div>
                          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[var(--rl-text-secondary)] mt-2 flex items-center gap-2">
                            <span>— Anonymized panelist</span>
                            {hasAudio && (
                              <span className="inline-flex items-center gap-1 text-[var(--rl-accent)]">
                                <Play className="w-2.5 h-2.5" /> Play audio
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Wave 12 — Emotional Tone & Sentiment Breakdown (text-classified) */}
        {data.emotions && data.emotions.total_classified > 0 && (
          <div className="mt-10" data-testid="emotions-section">
            <div className="flex items-baseline justify-between mb-4">
              <div className="text-xs uppercase tracking-[0.3em] font-bold text-[var(--rl-accent)]">Emotional Tone & Sentiment Breakdown</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--rl-text-secondary)]" data-testid="emotions-count">
                {data.emotions.total_classified} qualitative utterances classified
              </div>
            </div>
            <div className="grid lg:grid-cols-3 gap-4">
              {/* NSS meter */}
              <div className="bg-white border border-[var(--rl-border)] p-5" data-testid="nss-card">
                <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[var(--rl-text-secondary)]">Net Sentiment Score</div>
                <div className="font-display font-black text-4xl mt-2" data-testid="nss-value" style={{ color: data.emotions.net_sentiment_score >= 0 ? "#00C853" : "#B00020" }}>
                  {data.emotions.net_sentiment_score >= 0 ? "+" : ""}{data.emotions.net_sentiment_score.toFixed(2)}
                </div>
                <div className="text-xs text-[var(--rl-text-secondary)] mt-1">-1 distressed · +1 hopeful</div>
                <div className="mt-4 relative h-2 bg-[var(--rl-bg-subtle)]">
                  <div className="absolute inset-y-0" style={{
                    left: `${data.emotions.net_sentiment_score >= 0 ? 50 : 50 + data.emotions.net_sentiment_score * 50}%`,
                    width: `${Math.abs(data.emotions.net_sentiment_score) * 50}%`,
                    background: data.emotions.net_sentiment_score >= 0 ? "#00C853" : "#B00020",
                    transition: "all 500ms ease",
                  }} />
                  <div className="absolute top-0 bottom-0 w-px bg-[var(--rl-text)]" style={{ left: "50%" }} />
                </div>
              </div>
              {/* Severity Index */}
              <div className="bg-white border border-[var(--rl-border)] p-5" data-testid="severity-card">
                <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[var(--rl-text-secondary)]">Severity Index</div>
                <div className="font-display font-black text-4xl mt-2" style={{ color: data.emotions.severity_index >= 4 ? "#B00020" : data.emotions.severity_index >= 3 ? "#FF3B30" : "#5E5F6E" }} data-testid="severity-value">
                  {data.emotions.severity_index}<span className="text-base font-normal text-[var(--rl-text-secondary)]"> / 5</span>
                </div>
                <div className="text-xs text-[var(--rl-text-secondary)] mt-1">1 mild · 5 acute distress</div>
                <div className="mt-4 flex gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <div key={n} className="flex-1 h-2" style={{
                      background: n <= data.emotions.severity_index ? (data.emotions.severity_index >= 4 ? "#B00020" : "#FF3B30") : "var(--rl-bg-subtle)",
                    }} />
                  ))}
                </div>
              </div>
              {/* Distribution bar */}
              <div className="bg-white border border-[var(--rl-border)] p-5" data-testid="emotion-distribution-card">
                <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[var(--rl-text-secondary)] mb-3">Tone Distribution</div>
                {Object.entries(data.emotions.distribution).map(([cat, n]) => {
                  const totalE = Object.values(data.emotions.distribution).reduce((s, x) => s + x, 0) || 1;
                  const pct = Math.round(100 * n / totalE);
                  return (
                    <div key={cat} className="mb-2" data-testid={`emotion-row-${cat}`}>
                      <div className="flex items-center justify-between text-[11px] mb-1">
                        <span className="font-semibold">{EMOTION_LABELS[cat] || cat}</span>
                        <span className="font-mono text-[var(--rl-text-secondary)]">{n} · {pct}%</span>
                      </div>
                      <div className="h-1.5 bg-[var(--rl-bg-subtle)]">
                        <div className="h-full" style={{ width: `${pct}%`, background: EMOTION_COLORS[cat], transition: "width 400ms ease" }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Exemplar quotes — top 3 categories */}
            <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="emotion-exemplars">
              {Object.entries(data.emotions.distribution)
                .sort((a, b) => b[1] - a[1])
                .filter(([, n]) => n > 0)
                .slice(0, 3)
                .map(([cat]) => {
                  const quotes = (data.emotions.exemplar_quotes && data.emotions.exemplar_quotes[cat]) || [];
                  if (quotes.length === 0) return null;
                  return (
                    <div key={cat} className="bg-white border border-[var(--rl-border)] p-5" data-testid={`emotion-exemplar-${cat}`}>
                      <div className="text-[10px] uppercase tracking-[0.3em] font-bold" style={{ color: EMOTION_COLORS[cat] }}>{EMOTION_LABELS[cat] || cat}</div>
                      {quotes.slice(0, 1).map((q, i) => (
                        <div key={i} className="mt-3 text-sm italic leading-relaxed">"{q.text}"</div>
                      ))}
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {/* Prosodic Emotional Timeline (Wave 5) — derived from audio acoustic features */}
        {data.prosody && data.prosody.count > 0 && (
          <div className="mt-10" data-testid="prosody-section">
            <div className="flex items-baseline justify-between mb-4">
              <div className="text-xs uppercase tracking-[0.3em] font-bold text-[var(--rl-brand)]">Prosodic Emotion Signal</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--rl-text-secondary)]" data-testid="prosody-count">{data.prosody.count} voiced turns analyzed</div>
            </div>

            {/* Aggregate cards: arousal / valence / confidence + label mix */}
            <div className="grid sm:grid-cols-3 gap-4 mb-6">
              {[
                { key: "avg_arousal", label: "Avg. Arousal", color: "var(--rl-accent)", testid: "prosody-avg-arousal" },
                { key: "avg_valence", label: "Avg. Valence", color: "var(--rl-brand)", testid: "prosody-avg-valence" },
                { key: "avg_confidence", label: "Avg. Confidence", color: "#00C853", testid: "prosody-avg-confidence" },
              ].map((s) => {
                const val = data.prosody[s.key];
                return (
                  <div key={s.key} className="bg-white border border-[var(--rl-border)] p-5" data-testid={s.testid}>
                    <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[var(--rl-text-secondary)]">{s.label}</div>
                    <div className="font-display font-black text-4xl mt-2" style={{ color: s.color }}>{val != null ? Math.round(val) : "—"}<span className="text-base font-normal text-[var(--rl-text-secondary)]">/100</span></div>
                    <div className="mt-3 h-1.5 bg-[var(--rl-bg-subtle)]">
                      <div className="h-full" style={{ width: `${val || 0}%`, background: s.color, transition: "width 600ms ease" }} />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Emotion label distribution */}
            {Object.keys(data.prosody.label_distribution || {}).length > 0 && (
              <div className="bg-white border border-[var(--rl-border)] p-5 mb-6" data-testid="prosody-label-mix">
                <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[var(--rl-text-secondary)] mb-3">Emotion Mix</div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(data.prosody.label_distribution).map(([lbl, n]) => (
                    <span key={lbl} className="inline-flex items-center gap-2 bg-[var(--rl-bg-subtle)] border border-[var(--rl-border)] px-3 py-1.5 text-xs" data-testid={`prosody-label-${lbl}`}>
                      <span className="font-semibold capitalize">{lbl}</span>
                      <span className="font-mono text-[var(--rl-text-secondary)]">{n}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Per-turn timeline strip — arousal (red) vs valence (blue) bars */}
            {(data.prosody.timeline || []).length > 0 && (
              <div className="bg-white border border-[var(--rl-border)] p-5" data-testid="prosody-timeline">
                <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[var(--rl-text-secondary)] mb-4">Per-Turn Timeline</div>
                <div className="overflow-x-auto">
                  <div className="flex items-end gap-1 min-w-full" style={{ height: "140px" }}>
                    {data.prosody.timeline.map((t, idx) => (
                      <div
                        key={`prosody-bar-${idx}`}
                        className="flex flex-col items-center flex-1 min-w-[18px] group relative cursor-help"
                        data-testid={`prosody-turn-${idx}`}
                        title={`Q${t.q_index ?? "?"} • ${t.label} • A:${Math.round(t.arousal||0)} V:${Math.round(t.valence||0)} • "${t.text_excerpt}"`}
                      >
                        <div className="flex items-end gap-0.5 h-[110px] w-full">
                          <div className="flex-1 bg-[var(--rl-accent)]" style={{ height: `${Math.max(2, t.arousal || 0)}%`, transition: "height 400ms ease" }} />
                          <div className="flex-1 bg-[var(--rl-brand)]" style={{ height: `${Math.max(2, t.valence || 0)}%`, transition: "height 400ms ease" }} />
                        </div>
                        <div className="text-[9px] font-mono text-[var(--rl-text-secondary)] mt-1">{t.q_index ?? "-"}</div>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-4 mt-3 text-[10px] uppercase tracking-[0.2em] font-bold text-[var(--rl-text-secondary)]">
                    <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 bg-[var(--rl-accent)]" /> Arousal</span>
                    <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 bg-[var(--rl-brand)]" /> Valence</span>
                    <span className="ml-auto text-[10px] normal-case tracking-normal text-[var(--rl-text-secondary)] italic">Hover a bar for the verbatim utterance</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Audio playback drawer — slides up when a quote is clicked */}
      {activeQuote && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-[var(--rl-text)] text-white border-t border-[var(--rl-accent)]" data-testid="audio-drawer">
          <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-4">
            <Volume2 className="w-5 h-5 text-[var(--rl-accent)] flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/60">Original audio quote</div>
              <div className="text-sm italic mt-1 truncate">"{activeQuote.text}"</div>
            </div>
            <audio
              ref={audioRef}
              src={`${API}/files/audio/${activeQuote.interview_id}/${activeQuote._filename}`}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onEnded={() => setPlaying(false)}
              controls
              className="h-9"
              data-testid="audio-element"
            />
            <button onClick={() => { setActiveQuote(null); setPlaying(false); }} className="text-white/60 hover:text-white" data-testid="audio-drawer-close">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
      {/* Insight Agent drawer — bottom-right floating button → chat */}
      <InsightChat campaignId={campaignId} />
    </div>
  );
}
